import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Typography, useMediaQuery } from "@mui/material";
import { motion } from "framer-motion";
import images from "../../../assets/images";
import { OnboardingArrowRightIcon } from "../../../assets/icon";
import { DotsIndicator } from "../dots-indicator-onboarding/dots-indicator-onboarding";
import { usePreloadImages } from "../../../hooks/usePreloadImages";

interface InfoOnboardingProps {
  handlePrevPage: () => void;
  handleNextPage: () => void;
  handleSkip: () => void;
}

export const InfoOnboarding: React.FC<InfoOnboardingProps> = ({
  handlePrevPage,
  handleNextPage,
  handleSkip,
}) => {
  const isMobile = useMediaQuery("(max-width:600px)");
  const isCompactLandscape = useMediaQuery(
    "(orientation: landscape) and (max-width: 1366px)",
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const infoImage = isMobile
    ? images.OnboardingChatInfoMB
    : images.OnboardingChatInfoDS;

  const imagesLoaded = usePreloadImages([infoImage]);

  useEffect(() => {
    const checkSize = () => {
      if (contentRef.current && containerRef.current) {
        const containerHeight = containerRef.current.clientHeight;
        const containerWidth = containerRef.current.clientWidth;
        const contentHeight = contentRef.current.scrollHeight;
        const contentWidth = contentRef.current.scrollWidth;
        const safetyMargin = isCompactLandscape ? 0.96 : 0.98;
        const heightScale = containerHeight / contentHeight;
        const widthScale = containerWidth / contentWidth;
        const fittedScale = Math.min(heightScale, widthScale) * safetyMargin;
        const minScale = isCompactLandscape || isMobile ? 0 : 0.8;
        setScale(Math.max(Math.min(fittedScale, 1), minScale));
      }
    };

    if (imagesLoaded) {
      checkSize();
      window.addEventListener("resize", checkSize);
      window.addEventListener("orientationchange", checkSize);
      const timer = setTimeout(checkSize, 300);
      return () => {
        window.removeEventListener("resize", checkSize);
        window.removeEventListener("orientationchange", checkSize);
        clearTimeout(timer);
      };
    }
  }, [imagesLoaded, isCompactLandscape, isMobile]);

  if (!imagesLoaded) return null;

  return (
    <Box
      ref={containerRef}
      sx={{
        overflowX: "hidden",
        position: "relative",
        height: "100dvh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-around",
        width: "100%",
        alignItems: "center",
        flex: 1,
        overflowY: isCompactLandscape || isMobile ? "hidden" : "auto",
          scrollbarGutter: isCompactLandscape || isMobile ? "auto" : "stable",
      }}
    >
      <Box
        ref={contentRef}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: isMobile ? "10px" : isCompactLandscape ? "14px" : "32px",
          transform: `scale(${scale})`,
          transformOrigin: "center center",
          width: "100%",
          maxWidth: { xs: "100%", md: "980px" },
          marginX: "auto",
        }}
      >
        <Box
          component={motion.img}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          sx={{
            maxWidth: isCompactLandscape
              ? { xs: "260px", md: "400px" }
              : { xs: "280px", sm: "440px", md: "620px", lg: "560px" },
            width: "100%",
            padding: "8px",
          }}
          src={infoImage}
          alt="Info Onboarding"
        />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            maxWidth: { xs: "300px", md: "385px" },
            gap: "12px",
            paddingX: { xs: "12px", sm: "20px" },
          }}
        >
          <Typography
            component={motion.div}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            sx={{
              fontSize: isCompactLandscape
                ? { xs: "20px", md: "26px" }
                : { xs: "32px", md: "40px" },
              fontWeight: 700,
              textAlign: "center",
              whiteSpace: { md: "nowrap" },
              maxWidth: "100%",
            }}
          >
            Interaction Management
          </Typography>
          <Typography
            component={motion.div}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            sx={{
              color: "#84909F",
              fontSize: isCompactLandscape
                ? { xs: "12px", md: "14px" }
                : { xs: "16px", md: "18px" },
              textAlign: "center",
              paddingX: { xs: "6px", sm: "20px" },
            }}
          >
            Open the user menu and select{" "}
            <span style={{ color: "#28C9DC" }}>Interaction Management</span> to
            view favorites, lessons, tags, and more
          </Typography>
        </Box>
        <Button
          onClick={handleNextPage}
          sx={{
            marginTop: isMobile ? "6px" : isCompactLandscape ? "10px" : "32px",
            fontSize: isCompactLandscape ? "14px" : "18px",
            background: "linear-gradient(90deg, #28C9DC 0%, #8957CF 100%)",
            color: "#FFFFFF",
            textTransform: "none",
            fontWeight: "bold",
            borderRadius: "999px",
            px: isCompactLandscape ? "22px" : "32px",
            py: isCompactLandscape ? "9px" : "16px",
            "&:hover": {
              background: "linear-gradient(90deg, #20B6C8 0%, #7C4ABF 100%)",
            },
          }}
        >
          Next
        </Button>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            width: "100%",
            alignItems: "center",
            marginBottom: { xs: "8px", sm: "10px" },
            marginTop: { xs: "10px", sm: "12px" },
            maxWidth: "385px",
            paddingX: { xs: "8px", sm: "12px" },
          }}
        >
          <OnboardingArrowRightIcon
            style={{ width: "30px", cursor: "pointer" }}
            onClick={handlePrevPage}
          />
          <DotsIndicator activeIndex={6} />
          <Typography
            onClick={handleSkip}
            sx={{ fontSize: "16px", color: "#B6BAD5", cursor: "pointer" }}
          >
            Skip
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
